import eyeIcon from "@loot-os/icons/icons/eye";
import eyeOffIcon from "@loot-os/icons/icons/eye-off";
import type { IconData } from "@loot-os/icons/types";
import { type ComponentProps, type ReactNode, useState } from "react";
import { cn } from "../foundations/cn.js";
import { FOCUS_RING } from "../foundations/interaction.js";
import { Field } from "./Field.js";
import { Icon } from "./Icon.js";
import { FIELD_TEXT, fieldWellStyles } from "./Input.styles.js";

export interface InputProps extends Omit<ComponentProps<"input">, "className" | "size"> {
  /** Shown above the field and used as its name. Always required, even when hidden. */
  label: string;
  /** Hides the label visually (a search field); screen readers still hear it. */
  hideLabel?: boolean;
  /** One line under the field: format, limits, what happens next. */
  helper?: string;
  /** What's wrong, in words. Replaces the helper and marks the field invalid. */
  error?: string;
  /** Marks the field invalid without words, where the problem is plain (an amount that isn't one). */
  invalid?: boolean;
  /** An icon inside the field, before the text (a search glass). */
  icon?: IconData;
  /**
   * Something inside the field, before the text, that isn't an icon: a currency's mark before
   * an amount. Left out, the text starts at the field's edge.
   */
  leading?: ReactNode;
  /** Something inside the field, after the text: a unit, a clear button. */
  trailing?: ReactNode;
  /** Something on the label's line, after it: a link ("Forgot it?"). */
  labelEnd?: ReactNode;
  /** Mono with tabular digits, for money and anything compared. */
  mono?: boolean;
  /**
   * `type="password"` only: the name of the button that shows what was typed. It is a toggle
   * (`aria-pressed`), so the name stays the same whether or not the password shows.
   */
  showPasswordLabel?: string;
  size?: "sm" | "md" | "lg";
  className?: string;
}

/** The button a password field gets: shows and hides what was typed. */
function ShowPassword({
  label,
  shown,
  disabled,
  onToggle,
}: {
  label: string;
  shown: boolean;
  disabled?: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      aria-pressed={shown}
      disabled={disabled}
      onClick={onToggle}
      className={cn(
        "-mr-1.5 inline-flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-sm text-text-tertiary hover:text-text-primary disabled:cursor-default disabled:text-text-disabled",
        FOCUS_RING,
      )}
    >
      <Icon icon={shown ? eyeOffIcon : eyeIcon} />
    </button>
  );
}

/**
 * Text or number entry with its label, and a helper or error below. `type="password"` adds a
 * button that shows what was typed and turns off spellcheck and autocapitalize, so a shown
 * password never reaches the OS dictionary.
 */
export function Input({
  label,
  hideLabel,
  helper,
  error,
  invalid,
  icon,
  leading,
  trailing,
  labelEnd,
  mono = false,
  showPasswordLabel = "Show password",
  size,
  id,
  type,
  disabled,
  className,
  ...props
}: InputProps) {
  const [passwordShown, setPasswordShown] = useState(false);
  const password = type === "password";
  return (
    <Field
      label={label}
      hideLabel={hideLabel}
      helper={helper}
      error={error}
      invalid={invalid}
      labelEnd={labelEnd}
      id={id}
      className={className}
    >
      {(field) => (
        <div
          className={fieldWellStyles({ size, invalid: field.invalid, search: type === "search" })}
        >
          {icon && <Icon icon={icon} className="shrink-0 text-text-tertiary" />}
          {leading}
          <input
            id={field.id}
            type={password && passwordShown ? "text" : type}
            disabled={disabled}
            aria-invalid={field.invalid || undefined}
            aria-describedby={field.describedBy}
            {...(password && {
              autoComplete: "current-password",
              spellCheck: false,
              autoCapitalize: "off",
            })}
            className={cn(
              "h-full min-w-0 flex-1 [&::-webkit-search-cancel-button]:hidden",
              FIELD_TEXT,
              mono ? "font-mono tabular-nums" : "font-body",
            )}
            {...props}
          />
          {trailing}
          {password && (
            <ShowPassword
              label={showPasswordLabel}
              shown={passwordShown}
              disabled={disabled}
              onToggle={() => setPasswordShown((shown) => !shown)}
            />
          )}
        </div>
      )}
    </Field>
  );
}
